import React from 'react';
import classNames from 'classnames';

import { Responsive, responsiveClasses } from '../helpers/responsive';
import { FgColor, fgColorClass } from '../helpers/foregroundColors';
import { SizeStep, isSizeStep, sizeClass } from '../helpers/sizeScale';

// Relative-sizing modes (not on the fixed `size-*` scale): `fullWidth` fills the
// container, `inline` tracks the surrounding font size, `inline-prominent` is a larger
// inline glyph for emphasis, `button` takes its size from the enclosing button. These stay
// scalar — only the fixed `size-*` scale is responsive.
type IconSizeMode = 'fullWidth' | 'inline' | 'inline-prominent' | 'button';

export interface RCIconProps {
  /** A React icon component, `<svg>` element, or similar. */
  icon: JSX.Element;
  /** The rendered size of the icon.
   * - A step on the `size-*` scale (e.g. `size="6"` = 24px) — the primary API.
   *   Responsive: pass a value or a `{ desktop, tablet, mobile }` object.
   * - `fullWidth` – Default. Fills 100% of the container's width.
   * - `inline` – Sizes relative to the surrounding font size.
   * - `inline-prominent` – A larger inline glyph for emphasis (an icon leading a title, a
   *   link-out arrow), sized relative to the surrounding font.
   * - `button` – Sized by the enclosing button, for an icon placed inside a button-styled
   *   element (RCChip, a `.button`, a native `<button>`). Falls back to the `inline-prominent`
   *   size when no button sets one.
  */
  size?: Responsive<SizeStep> | IconSizeMode;
  /** @deprecated use `size = 'inline'` instead. Using this props overrides `size` prop. */
  inline?: boolean;
  /** The icon's color, from the shared foreground palette. Icons clear the 3:1 graphical-
   *  contrast bar, so both the text tier (4.5:1) and the accent tier (3:1) are available, plus
   *  the surface-tracking `text-themed` / `accent-themed` values — inside a themed ancestor
   *  (RCThemedBox / RCCallout / `.themed-box`) those take the surface's family color, and
   *  inherit otherwise. Responsive: pass a value or a `{ desktop, tablet, mobile }` object. */
  color?: Responsive<FgColor>;
  /** Adds a small em-relative gap (0.25em) on one side, to separate the icon from adjacent
   *  inline text. `left` spaces the icon's left edge, `right` its right edge — pick the side
   *  facing the text. For anything beyond this single gap, use `className`. */
  spacing?: 'left' | 'right';
  /** Added as an `aria-label`. Leave this blank for decorative icons, but add a descriptive verb for interactive icons.
   *  Icons without a `title` wll be `aria-hidden={true}`
  */
  title?: string;
  /** Escape hatch for styles the typed props don't cover (custom sizing, layout).
   *  Appended after the component's own classes, so it wins on equal-specificity collisions. */
  className?: string;
}

/**
 * A simple wrapper for SVG elements that handles icon sizing and accessibility.
 *
 * Icons are sized to 100% of their container's width by default.
*/
export function RCIcon({
  size = 'fullWidth',
  inline = false,
  color = undefined,
  spacing = undefined,
  title = undefined,
  className = undefined,
  icon,
}: RCIconProps) {
  // The deprecated `inline` boolean still wins over `size` when set.
  const resolvedSize: RCIconProps['size'] = inline ? 'inline' : size;

  const classes = classNames(
    'svg-icon',
    {
      'svg-icon--inline': resolvedSize === 'inline',
      'svg-icon--inline-prominent': resolvedSize === 'inline-prominent',
      'svg-icon--button': resolvedSize === 'button',
    },
    // Scale steps → width utility classes; the relative modes carry no width class
    // and fall through to the base/mode CSS.
    responsiveClasses(resolvedSize, widthSizeClass),
    responsiveClasses(color, fgColorClass),
    {
      'ml-space': spacing === 'left',
      'mr-space': spacing === 'right',
    },
    className,
  );

  return (
    <span
      className={classes}
      aria-hidden={title ? undefined : true}
      aria-label={title || undefined}
      role={title ? 'img' : undefined}
    >
      {React.isValidElement(icon) &&
        React.cloneElement(icon, { 'aria-hidden': true } as React.HTMLAttributes<Element>)
      }
    </span>
  );
}

// Maps one `size` value at a breakpoint to its width utility class, or `false` for the
// relative modes (`fullWidth` / `inline` / `inline-prominent` / `button`), which have no fixed width.
function widthSizeClass(size: SizeStep | IconSizeMode, prefix: string): string | false {
  if (isSizeStep(size)) return sizeClass('width', size, prefix);
  return false;
}
